import React, { useState, useRef, useEffect } from 'react';
import { FaPlus, FaTrash, FaEye, FaEdit, FaTimes } from 'react-icons/fa';
import TranslationButton from '../../ui/TranslationButton/TranslationButton';
import translationService from '../../../services/translationService';
import styles from './EnhancedProductFormWithTranslation.module.css';

// Size guide data
const SIZE_GUIDE = {
  XS: { cm: { chest: '76-81', waist: '58-63', hips: '81-86' }, inch: { chest: '30-32', waist: '23-25', hips: '32-34' } },
  S: { cm: { chest: '81-86', waist: '63-68', hips: '86-91' }, inch: { chest: '32-34', waist: '25-27', hips: '34-36' } },
  M: { cm: { chest: '86-91', waist: '68-73', hips: '91-96' }, inch: { chest: '34-36', waist: '27-29', hips: '36-38' } },
  L: { cm: { chest: '91-96', waist: '73-78', hips: '96-101' }, inch: { chest: '36-38', waist: '29-31', hips: '38-40' } },
  XL: { cm: { chest: '96-101', waist: '78-83', hips: '101-106' }, inch: { chest: '38-40', waist: '31-33', hips: '40-42' } },
  XXL: { cm: { chest: '101-106', waist: '83-88', hips: '106-111' }, inch: { chest: '40-42', waist: '33-35', hips: '42-44' } }
};

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

export default function EnhancedProductFormWithTranslation({ 
  onSubmit, 
  onCancel,
  initialData = null, 
  categories = [], 
  loading = false 
}) {
  const [productData, setProductData] = useState({
    name: initialData?.name || '',
    price: initialData?.price || '',
    description: initialData?.description || '',
    category: initialData?.category || '',
    mainImage: initialData?.mainImage || '',
    additionalImages: initialData?.additionalImages || [],
    sizes: initialData?.sizes || [],
    colors: initialData?.colors || [],
    isOffer: initialData?.isOffer || false,
    offerPrice: initialData?.offerPrice || '',
    isAman: initialData?.isAman || false,
    amanPrice: initialData?.amanPrice || '',
    wholesaleDiscount: initialData?.wholesaleDiscount || ''
  });

  // Translation state
  const [translatedData, setTranslatedData] = useState({
    name: { ar: '', en: '', tr: '' },
    description: { ar: '', en: '', tr: '' }
  });

  // Update form data when initialData changes (for editing)
  useEffect(() => {
    if (initialData) {
      setProductData({
        name: initialData.name || '',
        price: initialData.price || '',
        description: initialData.description || '',
        category: initialData.category || '',
        mainImage: initialData.mainImage || '',
        additionalImages: initialData.additionalImages || [],
        sizes: initialData.sizes || [],
        colors: initialData.colors || [],
        isOffer: initialData.isOffer || false,
        offerPrice: initialData.offerPrice || '',
        isAman: initialData.isAman || false,
        amanPrice: initialData.amanPrice || '',
        wholesaleDiscount: initialData.wholesaleDiscount || ''
      });

      // Load existing translations if available
      if (initialData.translations) {
        setTranslatedData(initialData.translations);
      }
    } else {
      // Reset form when not editing
      setProductData({
        name: '',
        price: '',
        description: '',
        category: '',
        mainImage: '',
        additionalImages: [],
        sizes: [],
        colors: [],
        isOffer: false,
        offerPrice: '',
        isAman: false,
        amanPrice: '',
        wholesaleDiscount: ''
      });
      setTranslatedData({
        name: { ar: '', en: '', tr: '' },
        description: { ar: '', en: '', tr: '' }
      });
    }
  }, [initialData]);

  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [newColor, setNewColor] = useState('');
  const mainImageInputRef = useRef();
  const additionalImagesInputRef = useRef();

  const handleInputChange = (field, value) => {
    setProductData(prev => ({
      ...prev,
      [field]: value
    }));

    // Update translated data for translatable fields
    if (['name', 'description'].includes(field)) {
      setTranslatedData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          ar: value
        }
      }));
    }
  };

  // Handle translation for a specific field
  const handleTranslateField = (fieldName, translations) => {
    setTranslatedData(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        en: translations.en,
        tr: translations.tr
      }
    }));
  };

  const handleSizeToggle = (size) => {
    setProductData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImageUploading(true);
    try {
      const url = await uploadImage(file);
      handleInputChange('mainImage', url);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setImageUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setImageUploading(true);
    try {
      const uploadPromises = files.map(file => uploadImage(file));
      const urls = await Promise.all(uploadPromises);
      setProductData(prev => ({
        ...prev,
        additionalImages: [...prev.additionalImages, ...urls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setImageUploading(false);
    }
  };

  const uploadImage = async (file) => {
    const CLOUD_NAME = 'dpwuxopjb';
    const UPLOAD_PRESET = '2nice-preset';
    const FOLDER = '2nice_folder';

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);
    formData.append('folder', FOLDER);

    const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
      method: 'POST',
      body: formData
    });

    const data = await response.json();
    if (!data.secure_url) {
      throw new Error('Upload failed');
    }
    return data.secure_url;
  };

  const removeAdditionalImage = (index) => {
    setProductData(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index)
    }));
  };

  const addColor = () => {
    if (newColor.trim() && !productData.colors.includes(newColor.trim())) {
      setProductData(prev => ({
        ...prev,
        colors: [...prev.colors, newColor.trim()]
      }));
      setNewColor('');
    }
  };

  const removeColor = (colorToRemove) => {
    setProductData(prev => ({
      ...prev,
      colors: prev.colors.filter(color => color !== colorToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (productData.sizes.length === 0) {
      alert('Please select at least one size');
      return;
    }

    // Combine product data with translations
    const finalProductData = {
      ...productData,
      translations: translatedData
    };

    onSubmit(finalProductData);
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formHeader}>
          <h2>{initialData ? 'Edit Product' : 'Add New Product'}</h2>
          <p>Create a new clothing product with multiple images, sizes, and translations</p>
        </div>

        {/* Basic Information */}
        <div className={styles.section}>
          <h3>Basic Information</h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Product Name *</label>
              <div className={styles.inputWithTranslation}>
                <input
                  type="text"
                  value={productData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter product name"
                  required
                />
                <TranslationButton
                  text={productData.name}
                  onTranslate={(translations) => handleTranslateField('name', translations)}
                  size="small"
                />
              </div>
              {translatedData.name.en && (
                <div className={styles.translationResults}>
                  <div className={styles.translationResult}>
                    <span className={styles.translationLabel}>EN:</span>
                    <span className={styles.translationText}>{translatedData.name.en}</span>
                  </div>
                  <div className={styles.translationResult}>
                    <span className={styles.translationLabel}>TR:</span>
                    <span className={styles.translationText}>{translatedData.name.tr}</span>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Category *</label>
              <select
                value={productData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                required
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Price (USD) *</label>
              <input
                type="number"
                value={productData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                placeholder="0"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Description *</label>
              <div className={styles.inputWithTranslation}>
                <textarea
                  value={productData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your product..."
                  rows="4"
                  required
                />
                <TranslationButton
                  text={productData.description}
                  onTranslate={(translations) => handleTranslateField('description', translations)}
                  size="small"
                />
              </div>
              {translatedData.description.en && (
                <div className={styles.translationResults}>
                  <div className={styles.translationResult}>
                    <span className={styles.translationLabel}>EN:</span>
                    <span className={styles.translationText}>{translatedData.description.en}</span>
                  </div>
                  <div className={styles.translationResult}>
                    <span className={styles.translationLabel}>TR:</span>
                    <span className={styles.translationText}>{translatedData.description.tr}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rest of the form remains the same */}
        {/* Images Section */}
        <div className={styles.section}>
          <h3>Product Images</h3>
          
          {/* Main Image */}
          <div className={styles.mainImageSection}>
            <label>Main Image (Displayed in shop) *</label>
            <div className={styles.imageUploadArea}>
              {productData.mainImage ? (
                <div className={styles.mainImagePreview}>
                  <img src={productData.mainImage} alt="Main product" />
                  <button
                    type="button"
                    className={styles.changeImageBtn}
                    onClick={() => mainImageInputRef.current?.click()}
                    disabled={imageUploading}
                  >
                    <FaEdit /> Change Image
                  </button>
                </div>
              ) : (
                <div 
                  className={styles.uploadPlaceholder}
                  onClick={() => mainImageInputRef.current?.click()}
                >
                  <FaPlus />
                  <p>Click to upload main image</p>
                </div>
              )}
              <input
                ref={mainImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleMainImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Additional Images */}
          <div className={styles.additionalImagesSection}>
            <label>Additional Images (Color variations, details)</label>
            <div className={styles.additionalImagesGrid}>
              {productData.additionalImages.map((image, index) => (
                <div key={index} className={styles.additionalImageItem}>
                  <img src={image} alt={`Product ${index + 1}`} />
                  <button
                    type="button"
                    className={styles.removeImageBtn}
                    onClick={() => removeAdditionalImage(index)}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <div 
                className={styles.addImageBtn}
                onClick={() => additionalImagesInputRef.current?.click()}
              >
                <FaPlus />
                <span>Add Image</span>
              </div>
            </div>
            <input
              ref={additionalImagesInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleAdditionalImageUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* Sizes Section */}
        <div className={styles.section}>
          <div className={styles.sizesHeader}>
            <h3>Available Sizes *</h3>
            <button
              type="button"
              className={styles.sizeGuideBtn}
              onClick={() => setShowSizeGuide(true)}
            >
              <FaEye /> Size Guide
            </button>
          </div>
          
          <div className={styles.sizesGrid}>
            {SIZES.map(size => (
              <button
                key={size}
                type="button"
                className={`${styles.sizeBtn} ${productData.sizes.includes(size) ? styles.selected : ''}`}
                onClick={() => handleSizeToggle(size)}
              >
                {size}
              </button>
            ))}
          </div>
          
          {productData.sizes.length > 0 && (
            <p className={styles.selectedSizes}>
              Selected: {productData.sizes.join(', ')}
            </p>
          )}
        </div>

        {/* Colors Section */}
        <div className={styles.section}>
          <h3>Available Colors</h3>
          <div className={styles.colorInputSection}>
            <div className={styles.colorInputGroup}>
              <input
                type="text"
                placeholder="Enter color name (e.g., Red, Blue, Black)"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addColor()}
                className={styles.colorInput}
              />
              <button
                type="button"
                onClick={addColor}
                className={styles.addColorBtn}
                disabled={!newColor.trim()}
              >
                <FaPlus /> Add Color
              </button>
            </div>
          </div>
          
          {productData.colors.length > 0 && (
            <div className={styles.colorsGrid}>
              {productData.colors.map((color, index) => (
                <div key={index} className={styles.colorItem}>
                  <span className={styles.colorName}>{color}</span>
                  <button
                    type="button"
                    onClick={() => removeColor(color)}
                    className={styles.removeColorBtn}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pricing Options */}
        <div className={styles.section}>
          <h3>Pricing Options</h3>
          <div className={styles.pricingOptions}>
            <div className={styles.offerOption}>
              <label>
                <input
                  type="checkbox"
                  checked={productData.isOffer}
                  onChange={(e) => handleInputChange('isOffer', e.target.checked)}
                />
                This is an offer product
              </label>
              {productData.isOffer && (
                <input
                  type="number"
                  placeholder="Offer Price (USD)"
                  value={productData.offerPrice}
                  onChange={(e) => handleInputChange('offerPrice', e.target.value)}
                  min="0"
                  step="0.01"
                  className={styles.offerPriceInput}
                />
              )}
            </div>

            <div className={styles.amanOption}>
              <label>
                <input
                  type="checkbox"
                  checked={productData.isAman}
                  onChange={(e) => handleInputChange('isAman', e.target.checked)}
                />
                This is an Aman product (store only)
              </label>
              {productData.isAman && (
                <input
                  type="number"
                  placeholder="Aman Price (USD)"
                  value={productData.amanPrice}
                  onChange={(e) => handleInputChange('amanPrice', e.target.value)}
                  min="0"
                  step="0.01"
                  className={styles.amanPriceInput}
                />
              )}
            </div>

            <div className={styles.wholesaleOption}>
              <label>Wholesale Discount (%)</label>
              <input
                type="number"
                placeholder="0"
                value={productData.wholesaleDiscount}
                onChange={(e) => handleInputChange('wholesaleDiscount', e.target.value)}
                min="0"
                max="100"
                step="0.01"
                className={styles.wholesaleInput}
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          <button type="submit" disabled={loading} className={styles.submitBtn}>
            {loading ? 'Saving...' : (initialData ? 'Update Product' : 'Add Product')}
          </button>
          <button type="button" onClick={onCancel} className={styles.cancelBtn}>
            Cancel
          </button>
        </div>
      </form>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className={styles.modalOverlay} onClick={() => setShowSizeGuide(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Size Guide</h3>
              <button onClick={() => setShowSizeGuide(false)} className={styles.closeBtn}>
                <FaTimes />
              </button>
            </div>
            <div className={styles.sizeGuideContent}>
              <table className={styles.sizeGuideTable}>
                <thead>
                  <tr>
                    <th>Size</th>
                    <th>Chest (cm)</th>
                    <th>Waist (cm)</th>
                    <th>Hips (cm)</th>
                    <th>Chest (inch)</th>
                    <th>Waist (inch)</th>
                    <th>Hips (inch)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(SIZE_GUIDE).map(([size, measurements]) => (
                    <tr key={size}>
                      <td>{size}</td>
                      <td>{measurements.cm.chest}</td>
                      <td>{measurements.cm.waist}</td>
                      <td>{measurements.cm.hips}</td>
                      <td>{measurements.inch.chest}</td>
                      <td>{measurements.inch.waist}</td>
                      <td>{measurements.inch.hips}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 